import type { Sprite, Container } from 'pixi.js';
import { Ticker } from 'pixi.js';

type LerperConfig = {
  displayObject: Sprite | Container;
  initValue: number;
  lerp?: number;
  snap?: number;
  onChange?: (value: number) => unknown;
  skipInitOnChange?: boolean;
};

function linear(p0: number, p1: number, progress: number): number {
  return (p1 - p0) * progress + p0;
}

export default class Lerper {
  private readonly lerp: number;

  private readonly snap: number;

  private ticker: Ticker | null = new Ticker();

  private value: number;

  private targetValue: number;

  constructor(private readonly config: LerperConfig) {
    this.lerp = config.lerp ?? 0.5;
    this.snap = config.snap ?? 0.01;
    this.value = config.initValue;
    this.targetValue = config.initValue;

    this.ticker?.add(() => {
      this.update();
    });
    config.displayObject.once('destroyed', () => {
      this.ticker?.destroy();
      this.ticker = null;
    });

    if (!config.skipInitOnChange) {
      this.config?.onChange?.(this.value);
    }
  }

  public setTarget(value: number, force = false): void {
    if (this.config.displayObject.destroyed) return;

    if (this.targetValue === value) return;

    if (force) {
      this.value = value;
      this.targetValue = value;
      this.config?.onChange?.(this.value);
      return;
    }

    this.targetValue = value;
    this.ticker?.start();
  }

  private update(): void {
    this.value = linear(this.value, this.targetValue, this.lerp);

    if (Math.abs(this.value - this.targetValue) <= this.snap) {
      this.value = this.targetValue;
    }

    this.config?.onChange?.(this.value);

    if (this.value === this.targetValue) {
      this.ticker?.stop();
    }
  }
}
